import { addDays, DateOnly } from '@if/core/date'
import { AggregationPeriod, MerchantLoanActivityMetric, METRIC_KEYS, MetricKey } from '@src/types/MerchantStatistic'
import { useMemo } from 'react'

const aggregateMetrics = (
  stats: MerchantLoanActivityMetric[],
  getKey: (date: Date) => string,
  getDisplayDate: (key: string) => string,
): MerchantLoanActivityMetric[] => {
  const aggregationMap = new Map<string, MerchantLoanActivityMetric>()

  stats.forEach((stat) => {
    const date = new Date(stat.date)
    const key = getKey(date)

    if (!aggregationMap.has(key)) {
      aggregationMap.set(key, {
        date: getDisplayDate(key),
        merchantId: stat.merchantId,
        totalApplications: 0,
        totalApplicationsAmount: 0,
        totalCreatedLoans: 0,
        totalGrantedAmount: 0,
        totalApprovedApplications: 0,
        totalMaxApprovalAmount: 0,
        totalPromoApplicationsWorksheet: 0,
        totalPromoApprovedWorksheetsAmountAfterMdr: 0,
      })
    }

    const aggregated = aggregationMap.get(key)!
    METRIC_KEYS.forEach((metricKey) => {
      aggregated[metricKey] += stat[metricKey] ?? 0
    })
  })

  return Array.from(aggregationMap.values()).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
}

export const useComputeLoanActivityMetricsAggregation = (
  merchantLoanActivityMetrics: MerchantLoanActivityMetric[],
  aggregation: AggregationPeriod,
): MerchantLoanActivityMetric[] => {
  return useMemo(() => {
    if (!merchantLoanActivityMetrics.length) return []

    switch (aggregation) {
      case AggregationPeriod.Daily:
        return merchantLoanActivityMetrics

      case AggregationPeriod.Weekly:
        return aggregateMetrics(
          merchantLoanActivityMetrics,
          (date) => {
            const weekStart = new Date(date)
            weekStart.setDate(date.getDate() - date.getDay())
            return weekStart.toISOString().split('T')[0]
          },
          (key) => key,
        )

      case AggregationPeriod.Monthly:
        return aggregateMetrics(
          merchantLoanActivityMetrics,
          (date) => `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`,
          (key) => `${key}-01`,
        )

      default:
        return merchantLoanActivityMetrics
    }
  }, [merchantLoanActivityMetrics, aggregation])
}

export type MetricTotals = Record<MetricKey, number>

export const sumMetrics = (stats: MerchantLoanActivityMetric[]): MetricTotals => {
  const totals = Object.fromEntries(METRIC_KEYS.map((key) => [key, 0])) as MetricTotals
  stats.forEach((stat) => {
    METRIC_KEYS.forEach((key) => {
      totals[key] += stat[key] ?? 0
    })
  })
  return totals
}

export const computeTrendPercent = (current: number, previous: number): number | null =>
  previous === 0 ? null : ((current - previous) / previous) * 100

export const getPreviousPeriodRange = (fromDate: string, toDate: string): { fromDate: string; toDate: string } => {
  const from = new DateOnly(fromDate)
  const to = new DateOnly(toDate)
  const spanDays = Math.round((to.getTime() - from.getTime()) / 86_400_000) + 1

  const prevTo = new Date(from)
  prevTo.setDate(prevTo.getDate() - 1)
  const prevFrom = new Date(prevTo)
  prevFrom.setDate(prevFrom.getDate() - (spanDays - 1))

  return {
    fromDate: DateOnly.fromDate(prevFrom).toString(),
    toDate: DateOnly.fromDate(prevTo).toString(),
  }
}

export enum QuickDatePreset {
  Last30Days = 'last30Days',
  Last90Days = 'last90Days',
  ThisMonth = 'thisMonth',
  ThisYear = 'thisYear',
}

export const getQuickDatePresetRange = (preset: QuickDatePreset): { fromDate: string; toDate: string } => {
  const today = DateOnly.today()
  const toDate = today.toString()

  switch (preset) {
    case QuickDatePreset.Last30Days: {
      const from = addDays(today, -29)
      return { fromDate: DateOnly.fromDate(from).toString(), toDate }
    }
    case QuickDatePreset.Last90Days: {
      const from = addDays(today, -89)
      return { fromDate: DateOnly.fromDate(from).toString(), toDate }
    }
    case QuickDatePreset.ThisMonth: {
      const from = DateOnly.fromComponents(today.getFullYear(), today.getMonth(), 1)
      return { fromDate: from.toString(), toDate }
    }
    case QuickDatePreset.ThisYear: {
      const from = DateOnly.fromComponents(today.getFullYear(), 0, 1)
      return { fromDate: from.toString(), toDate }
    }
  }
}
